/**
 * General utility helper functions
 * Extracted from App.js for better organization and reusability
 */

/**
 * Check if a user has super admin privileges
 * @param {Object} user - User object
 * @returns {boolean} - True if user is a super admin
 */
export const isSuperAdmin = (user) => {
  if (!user) return false;
  // Primary check: is_super_admin flag (most reliable)
  // Fallback: role field (always lowercase from backend)
  return user.is_super_admin === true || user.role === 'super admin';
};

/**
 * Check if user is a facilitator of any group
 * @param {Object} user - User object
 * @param {Array} groups - Array of group objects
 * @returns {boolean} - True if user facilitates any group
 */
export const isGroupFacilitator = (user, groups) => {
  if (!user || !groups || !Array.isArray(groups)) return false;
  return groups.some(g => g.facilitator_id === user.id);
};

/**
 * Safely get option label - handles both string options and object options {label, value, score}
 * @param {string|Object} opt - Option value (string or object with label/value)
 * @returns {string} - The label string
 */
export const getOptionLabel = (opt) => {
  if (!opt) return '';
  if (typeof opt === 'string') return opt;
  if (typeof opt === 'object') return opt.label || opt.value || '';
  return String(opt);
};

/**
 * Get POS codes with proper fallback
 * Handles the case where organization pos_codes is an empty array (which is truthy)
 * @param {Array} organizations - Array of organization objects
 * @param {Array} posCodes - Fallback POS codes array
 * @returns {Array} - Effective POS codes
 */
export const getEffectivePosCodes = (organizations, posCodes) => {
  const orgPosCodes = organizations?.[0]?.pos_codes;
  if (orgPosCodes && orgPosCodes.length > 0) {
    return orgPosCodes;
  }
  return posCodes || [];
};

/**
 * Format state name for consent language
 * Some states have special official names (e.g., "Commonwealth of Massachusetts")
 * @param {string} stateName - State name to format
 * @returns {string} - Formatted state name for legal documents
 */
export const formatStateForConsent = (stateName) => {
  // Handle null, undefined, or empty string - default to Massachusetts
  const effectiveState = (stateName && stateName.trim() !== '') ? stateName.trim() : 'Massachusetts';
  
  const commonwealthStates = {
    'Massachusetts': 'the Commonwealth of Massachusetts',
    'Pennsylvania': 'the Commonwealth of Pennsylvania',
    'Virginia': 'the Commonwealth of Virginia',
    'Kentucky': 'the Commonwealth of Kentucky',
  };
  
  return commonwealthStates[effectiveState] || effectiveState;
};
